import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, X, Send, Minimize2, Maximize2, Loader2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/shared/ui/button';
import { useCompany } from '../shared/context/CompanyContext';
import api from '../shared/services/api';

const AIPanel = () => {
  const { activeCompany } = useCompany();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { type: 'ai', message: "I'm ThreatLens AI, your security analyst. Ask me anything about your attack surface, vulnerabilities, or security posture." }
  ]);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Hide on the dedicated LensGuard AI page — that page IS the AI chat interface
  if (location.pathname === '/lensguard-ai') return null;

  const handleSendMessage = async () => {
    if (!chatInput.trim() || loading) return;
    if (!activeCompany) {
      setChatMessages(prev => [...prev, { type: 'user', message: chatInput }, { type: 'ai', message: 'Please add a company first to analyze its attack surface.' }]);
      setChatInput('');
      return;
    }

    const question = chatInput.trim();
    setChatMessages(prev => [...prev, { type: 'user', message: question }]);
    setChatInput('');
    setLoading(true);

    try {
      const resp = await api.aiQuery(activeCompany.company_id, question);
      setChatMessages(prev => [...prev, { type: 'ai', message: resp.answer }]);
    } catch (e) {
      const msg = e?.message || 'Please try again.';
      const friendly = msg.includes('fetch') || msg.includes('Connection') || msg.includes('Network')
        ? `Connection error. Check that the backend is running at ${process.env.REACT_APP_BACKEND_URL || 'the configured URL'} and that the AI service (e.g. Anthropic API key) is configured.`
        : `Analysis failed: ${msg}`;
      setChatMessages(prev => [...prev, { type: 'ai', message: friendly }]);
    } finally {
      setLoading(false);
    }
  };

  const quickQuestions = [
    "What's our biggest security risk?",
    "Which assets should I fix first?",
    "Summarize our threat posture",
    "What attack paths exist?",
  ];

  if (!isOpen) {
    return (
      <button onClick={() => setIsOpen(true)} data-testid="ai-panel-toggle"
        className="fixed right-6 bottom-6 z-[9999] w-14 h-14 bg-gradient-to-br from-purple-600 to-purple-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center group">
        <Sparkles className="w-6 h-6 group-hover:scale-110 transition-transform" />
      </button>
    );
  }

  return (
    <div className={`fixed right-6 bottom-6 z-[9999] bg-white dark:bg-navy-800 rounded-xl shadow-2xl border border-gray-200 dark:border-navy-700 transition-all ${
      isMinimized ? 'w-80 h-16' : 'w-96 h-[500px]'
    }`}>
      <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-navy-700 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-t-xl">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">ThreatLens AI</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Claude-powered analyst</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setIsMinimized(!isMinimized)} className="p-1.5 hover:bg-white dark:hover:bg-navy-700 rounded">
            {isMinimized ? <Maximize2 className="w-3.5 h-3.5 text-gray-500" /> : <Minimize2 className="w-3.5 h-3.5 text-gray-500" />}
          </button>
          <button onClick={() => setIsOpen(false)} className="p-1.5 hover:bg-white dark:hover:bg-navy-700 rounded">
            <X className="w-3.5 h-3.5 text-gray-500" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <div className="flex flex-col h-[calc(100%-3.5rem)]">
          <div className="flex-1 p-3 overflow-y-auto">
            <div className="space-y-3">
              {chatMessages.map((msg, i) => (
                <div key={i} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] p-3 rounded-lg text-sm whitespace-pre-wrap ${
                    msg.type === 'user'
                      ? 'bg-purple-600 text-white rounded-br-none'
                      : 'bg-gray-100 dark:bg-navy-700 text-gray-900 dark:text-white rounded-bl-none'
                  }`}>{msg.message}</div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 dark:bg-navy-700 p-3 rounded-lg rounded-bl-none">
                    <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          </div>

          <div className="p-3 border-t border-gray-200 dark:border-navy-700">
            <div className="flex gap-2">
              <input type="text" value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Ask about your security..." disabled={loading}
                data-testid="ai-chat-input"
                className="flex-1 px-3 py-2 text-sm bg-gray-50 dark:bg-navy-700 border border-gray-200 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50" />
              <Button variant="primary" size="sm" onClick={handleSendMessage} disabled={loading}
                data-testid="ai-send-btn" className="px-3 bg-purple-600 hover:bg-purple-700">
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {quickQuestions.map((q, i) => (
                <button key={i} onClick={() => { setChatInput(q); }}
                  className="text-xs text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded hover:bg-purple-100 transition-colors">
                  {q}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIPanel;
